import { type BooksHealthErrorCode, ErrorCode, isPlatformServiceErrorCode } from '@sahab/contracts'
import type { TFunction } from 'i18next'
import enCommon from '../locales/en/common.json'
import { type ApiError, ClientErrorCode, isApiError, PASSWORD_CHANGE_REQUIRED } from './api'
import { formatDate } from './format'

/** 422 PERIOD_LOCKED: an entry dated on or before the lock date (settings Q6). */
export const PERIOD_LOCKED = 'PERIOD_LOCKED'

/**
 * The Books health codes (`BOOKS_HEALTH_ERROR_CODES`) any screen may meet: 503 BOOKS_READ_TIMEOUT (the
 * drill-down or a dismissal's check ran out of time) and BOOKS_HEALTH_UNAVAILABLE. The others (a finding no
 * longer holding, a dismissal's date) are worded where they happen.
 */
const BOOKS_READ_ERROR_CODES = [
  'BOOKS_READ_TIMEOUT',
  'BOOKS_HEALTH_UNAVAILABLE',
] as const satisfies readonly BooksHealthErrorCode[]
export function isBooksReadErrorCode(code: string): code is (typeof BOOKS_READ_ERROR_CODES)[number] {
  return (BOOKS_READ_ERROR_CODES as readonly string[]).includes(code)
}

/** A problem code with a wording of its own in `common:errors.codes`, whichever screen meets it. */
type CommonErrorCode = keyof typeof enCommon.errors.codes
function isCommonErrorCode(code: string): code is CommonErrorCode {
  return Object.hasOwn(enCommon.errors.codes, code)
}

/** 422 PERIOD_CLOSED: an entry dated in a closed fiscal year. */
export const PERIOD_CLOSED = 'PERIOD_CLOSED'
/** 422 NO_FISCAL_YEAR: no fiscal year covers the entry's date (a gap between custom years). */
export const NO_FISCAL_YEAR = 'NO_FISCAL_YEAR'
/**
 * 422 BANK_LINE_MATCHED: voiding or undoing would reverse an entry whose bank line is cleared on a bank
 * statement (`statementNumber`, `statementCompleted`); it is unmatched there first. A ledger rule, so
 * every screen that voids (sales, purchasing, finance, cheques, the till) says it the same way.
 */
export const BANK_LINE_MATCHED = 'BANK_LINE_MATCHED'

/**
 * A period refusal (PERIOD_LOCKED, PERIOD_CLOSED, NO_FISCAL_YEAR) sent while Accounting is off for the
 * company (extension `accountingOff`): nobody in it can lift the refusal.
 */
export function accountingOff(error: ApiError): boolean {
  return error.extensions.accountingOff === true
}

const ISO_DATE = /\b(\d{4}-\d{2}-\d{2})\b/

/**
 * The lock date a PERIOD_LOCKED refusal names: an extension member when the API sends one, else the
 * first ISO date of its detail ("The books are locked up to 2026-06-30: …"). Null when neither says.
 */
export function lockedThrough(error: ApiError): string | null {
  const member = error.extensions.lockDate
  if (typeof member === 'string' && ISO_DATE.test(member)) return member
  return ISO_DATE.exec(error.detail ?? '')?.[1] ?? null
}

/**
 * A translated, human sentence for any error: what went wrong and what to do. Stable problem codes
 * map to translations; a server `detail` is only shown to English users for codes we do not know
 * (server details are English-only).
 */
export function errorMessage(t: TFunction<'common'>, language: string, error: unknown): string {
  if (!isApiError(error)) return t('errors.unexpected')
  switch (error.code) {
    case ClientErrorCode.NETWORK_ERROR:
      return t('errors.network')
    case ClientErrorCode.SERVICE_UNAVAILABLE:
      return t('errors.unavailable')
    case ErrorCode.UNAUTHENTICATED:
      return t('errors.unauthenticated')
    case ErrorCode.FORBIDDEN:
      return t('errors.forbidden')
    case ErrorCode.MODULE_DISABLED:
      return t('errors.moduleDisabled')
    case ErrorCode.NOT_FOUND:
      return t('errors.notFound')
    case ErrorCode.CONFLICT:
      return language === 'en' && error.detail ? error.detail : t('errors.conflict')
    case ErrorCode.VERSION_CONFLICT:
      return t('errors.versionConflict')
    case ErrorCode.RATE_LIMITED:
      return t('errors.rateLimited')
    case ErrorCode.VALIDATION_FAILED:
      return t('errors.validation')
    case PASSWORD_CHANGE_REQUIRED:
      return t('errors.passwordChangeRequired')
    case PERIOD_LOCKED: {
      // Posting, voiding or reversing anything dated on or before the lock date: say up to when,
      // and what to do. Every feature's error mapping falls through to here. With Accounting off
      // nobody in the company can move the lock date (ADR-024): the provider has to.
      const date = lockedThrough(error)
      const off = accountingOff(error)
      return date
        ? t(off ? 'errors.periodLockedOff' : 'errors.periodLocked', {
            date: formatDate(date, language === 'ar' ? 'ar' : 'en'),
          })
        : t(off ? 'errors.periodLockedNoDateOff' : 'errors.periodLockedNoDate')
    }
    case PERIOD_CLOSED:
      return t(accountingOff(error) ? 'errors.periodClosedOff' : 'errors.periodClosed')
    case NO_FISCAL_YEAR:
      return t(accountingOff(error) ? 'errors.noFiscalYearOff' : 'errors.noFiscalYear')
    case BANK_LINE_MATCHED: {
      const number = error.extensions.statementNumber
      if (typeof number !== 'string')
        return language === 'en' && error.detail ? error.detail : fallback(t, 422)
      return t(
        error.extensions.statementCompleted === true
          ? 'errors.bankLineMatchedCompleted'
          : 'errors.bankLineMatched',
        { number },
      )
    }
    case ErrorCode.INTERNAL:
      return t('errors.internal')
    default:
      // Platform-service rules (R0b: messaging, PDFs, privacy, residency) read the same on every screen.
      if (isPlatformServiceErrorCode(error.code)) return t(`errors.codes.${error.code}`)
      // AA1: reading the books for Books health or a drill-down took too long, or isn't reachable.
      if (isBooksReadErrorCode(error.code)) return t(`errors.codes.${error.code}`)
      // ADR-049: a document, customer, job or contract naming a salesperson who is not an active user.
      if (error.code === 'SALESPERSON_INVALID') return t('errors.codes.SALESPERSON_INVALID')
      // Any other code worded once for every screen (`errors.codes` in common): the fiscal close, a
      // journal's reversal, Books health, a product's reorder level (G22).
      if (isCommonErrorCode(error.code)) return t(`errors.codes.${error.code}`)
      if (language === 'en' && error.detail) return error.detail
      return fallback(t, error.status)
  }
}

function fallback(t: TFunction<'common'>, status: number): string {
  if (status === 409) return t('errors.conflict')
  if (status >= 500) return t('errors.internal')
  return t('errors.rejected')
}
